import {
	ASTEROID_MAX_ANGLE,
	ASTEROID_MAX_SPEED,
	ASTEROID_MAX_SPIN,
	ASTEROID_RADIUS,
	ASTEROID_SPAWN_INTERVAL,
	ASTEROID_SPAWN_RETRY_TICKS,
	ASTEROID_SPAWN_SHIP_CLEARANCE,
	FUEL_STATION_RADIUS,
	PICKUP_PICKUP_CLEARANCE,
	PICKUP_PLANET_CLEARANCE,
	PICKUP_SHIP_CLEARANCE,
	PICKUP_SPAWN_POINT_CLEARANCE,
	SURVIVAL_INTERVAL_STEP,
	SURVIVAL_MIN_INTERVAL,
	SURVIVAL_RAMP_TICKS,
} from "../constants";
import {
	createAsteroid,
	createPickup,
	createShip,
	type Entity,
	type PlayerIndex,
	type SpawnPoint,
} from "../entities";
import type { SimEvent } from "../events";
import { POWER_UP_KINDS, type PowerUpKind } from "../match-rules";
import * as DMath from "../math/det-math";
import { nextFloat } from "../math/rng";
import { wrappedDistance } from "../math/torus";
import type { WorldState } from "../state";
import { allocId, insertEntity, queueShip } from "../world";

/** legacy: WorldObjectsManager::performQueuedRemovals — runs at the start of each tick. */
export function performRemovals(world: WorldState, events: SimEvent[]): void {
	if (world.removeQueue.length === 0) return;
	const doomed = new Set(world.removeQueue);
	world.removeQueue = [];
	world.entities = world.entities.filter((e) => {
		if (!doomed.has(e.id)) return true;
		events.push({ type: "removed", id: e.id, kind: e.kind });
		return false;
	});
}

/**
 * legacy: WorldObjectsManager::performQueuedInsertions. Ships first, then asteroids, each
 * taking the next free spawn point in map order (occupancy comes from last tick's collision
 * pass). A spawn point used here counts as occupied for the rest of the insertion step.
 * With random spawn points (rules.spawnPoints) each spawn picks a random free point instead.
 * With safe asteroid spawns (I-027) asteroids take a random clear spot instead.
 */
export function performInsertions(world: WorldState, events: SimEvent[]): void {
	const spawnPoints = world.entities.filter((e): e is SpawnPoint => e.kind === "spawnPoint");
	const pick = world.rules.spawnPoints === null ? firstFree : randomFree;

	while (world.shipQueue.length > 0) {
		// Capture the Flag: every pilot respawns at their own base (waiting while it is occupied).
		const next = world.shipQueue[0] ?? -1;
		const sp = world.mode === "ctf" ? ownBase(world, spawnPoints, next) : pick(world, spawnPoints);
		if (!sp) break;
		const player = world.players[world.shipQueue.shift() ?? -1];
		if (!player) continue;
		const ship = createShip(allocId(world), player.index, player.color, sp.pos);
		ship.missiles = world.rules.missilesPerLife ?? 0;
		insertEntity(world, ship, events);
		player.shipId = ship.id;
		player.awaitingSpawn = false;
		sp.occupied = true;
	}

	if (world.rules.safeAsteroidSpawns) {
		insertSafeAsteroids(world, events);
		return;
	}
	while (world.asteroidQueue.length > 0) {
		const sp = pick(world, spawnPoints);
		const queued = sp && world.asteroidQueue.shift();
		if (!sp || !queued) break;
		insertEntity(world, createAsteroid(allocId(world), sp.pos, queued.vel, queued.spin), events);
		sp.occupied = true;
	}
}

/** Safe asteroid spawns (I-027): random clear spots; after a miss, wait before drawing again. */
function insertSafeAsteroids(world: WorldState, events: SimEvent[]): void {
	while (world.asteroidQueue.length > 0) {
		const queued = world.asteroidQueue[0];
		if (!queued) break;
		if (queued.retryIn !== undefined && queued.retryIn > 0) {
			queued.retryIn--;
			break;
		}
		const pos = findClearAsteroidSpot(world);
		if (!pos) {
			queued.retryIn = ASTEROID_SPAWN_RETRY_TICKS;
			break;
		}
		world.asteroidQueue.shift();
		insertEntity(world, createAsteroid(allocId(world), pos, queued.vel, queued.spin), events);
	}
}

/** The spawn point nearest to the player's flag base, if it is free. */
function ownBase(
	world: WorldState,
	points: SpawnPoint[],
	player: PlayerIndex,
): SpawnPoint | undefined {
	const objective = world.objective;
	const home = objective?.kind === "ctf" ? objective.flags[player]?.home : undefined;
	if (!home) return firstFree(world, points);
	let best: SpawnPoint | undefined;
	let bestDist = Number.POSITIVE_INFINITY;
	for (const sp of points) {
		const d = wrappedDistance(sp.pos, home, world.size);
		if (d < bestDist) {
			best = sp;
			bestDist = d;
		}
	}
	return best && !best.occupied ? best : undefined;
}

function firstFree(_world: WorldState, points: SpawnPoint[]): SpawnPoint | undefined {
	return points.find((sp) => !sp.occupied);
}

function randomFree(world: WorldState, points: SpawnPoint[]): SpawnPoint | undefined {
	const free = points.filter((sp) => !sp.occupied);
	if (free.length === 0) return undefined;
	return free[Math.floor(nextFloat(world.rng) * free.length)];
}

/**
 * legacy: AsteroidStrategy(200). Queues one asteroid when the countdown hits zero, so the
 * effective period is interval + 1 ticks. Random launch speed [0, 6), angle [0, 3.14) in
 * legacy y-up space (negated for y-down) and spin [-0.025, 0.025) (negated likewise).
 */
export function spawnAsteroids(world: WorldState): void {
	const field = world.asteroidField;
	if (field?.interval === null) return;
	if (world.asteroidCountdown > 0) {
		world.asteroidCountdown--;
		return;
	}
	// A map's asteroid limit (P11.12): wait at zero until one is gone (no random draws).
	if (field?.max != null && countAsteroids(world) >= field.max) return;
	const speed = (field?.maxSpeed ?? ASTEROID_MAX_SPEED) * nextFloat(world.rng);
	const angle = -ASTEROID_MAX_ANGLE * nextFloat(world.rng);
	const spin = -(2 * ASTEROID_MAX_SPIN * nextFloat(world.rng) - ASTEROID_MAX_SPIN);
	world.asteroidQueue.push({
		vel: { x: DMath.cos(angle) * speed, y: DMath.sin(angle) * speed },
		spin,
	});
	world.asteroidCountdown = asteroidInterval(world);
}

function countAsteroids(world: WorldState): number {
	let n = world.asteroidQueue.length;
	for (const e of world.entities) if (e.kind === "asteroid") n++;
	return n;
}

/**
 * Ticks between asteroids: the legacy 200 (or the map's own rate) in duels; in survival it
 * shrinks by SURVIVAL_INTERVAL_STEP every SURVIVAL_RAMP_TICKS, down to SURVIVAL_MIN_INTERVAL
 * (or the map's interval, if that is already shorter).
 */
export function asteroidInterval(world: WorldState): number {
	const base = world.asteroidField?.interval ?? ASTEROID_SPAWN_INTERVAL;
	if (world.mode !== "survival") return base;
	const steps = Math.floor(world.tick / SURVIVAL_RAMP_TICKS);
	return Math.max(Math.min(SURVIVAL_MIN_INTERVAL, base), base - steps * SURVIVAL_INTERVAL_STEP);
}

/** legacy: Player::tick — after a ship is lost, wait out the countdown, then queue the next one. */
export function tickPlayers(world: WorldState, events: SimEvent[]): void {
	for (const player of world.players) {
		if (player.shipId !== null || player.awaitingSpawn || player.shipsLeft <= 0) continue;
		if (player.respawnCountdown === 0) queueShip(world, player, events);
		else player.respawnCountdown--;
	}
}

/**
 * The power-up kinds that may spawn: enabled in the rules and useful in this match. Fuel
 * needs the fuel rule, extra missiles a missile limit, freeze an opponent, and repair the
 * hull rule. Homing pods carry their own missiles (added to the magazine alongside the
 * homing shots), so they're useful regardless of the missile limit.
 */
export function spawnablePowerUps(world: WorldState): PowerUpKind[] {
	const { rules } = world;
	const enabled = (kind: PowerUpKind) => rules.powerUpTypes[kind];
	const limited = rules.missilesPerLife !== null;
	return POWER_UP_KINDS.filter((kind) => {
		if (!enabled(kind)) return false;
		switch (kind) {
			case "fuel":
				return rules.fuel;
			case "extraMissiles":
				return limited;
			case "freeze":
				return world.players.length > 1;
			case "repair":
				return rules.damage;
			default:
				return true;
		}
	});
}

/**
 * Power-ups rule: after a random interval in [min, max] ticks, place a pod of a random
 * spawnable kind at a random spot clear of planets, ships, spawn points and other pods,
 * unless the map already holds the maximum. (Legacy queued items at spawn points but never
 * spawned any; open space makes them worth flying to.)
 */
export function spawnPickups(world: WorldState, events: SimEvent[]): void {
	const { rules } = world;
	if (!rules.powerUps) return;
	const kinds = spawnablePowerUps(world);
	if (kinds.length === 0) return;

	if (world.pickupCountdown > 0) {
		world.pickupCountdown--;
		return;
	}
	const spread = rules.powerUpIntervalMaxTicks - rules.powerUpIntervalMinTicks;
	world.pickupCountdown =
		rules.powerUpIntervalMinTicks + Math.floor(nextFloat(world.rng) * (spread + 1));
	if (world.entities.filter((e) => e.kind === "pickup").length >= rules.powerUpMax) return;

	const kind = kinds[Math.floor(nextFloat(world.rng) * kinds.length)] ?? "fuel";
	const pos = findClearSpot(world);
	if (pos) {
		const pod = createPickup(allocId(world), kind, pos, rules.powerUpLifetimeTicks);
		insertEntity(world, pod, events);
	}
}

/** How far a pod must stay from an entity (0 = no constraint). */
function pickupClearance(e: Entity): number {
	switch (e.kind) {
		case "planet":
			return e.radius + PICKUP_PLANET_CLEARANCE;
		case "ship":
			return PICKUP_SHIP_CLEARANCE;
		case "spawnPoint":
			return PICKUP_SPAWN_POINT_CLEARANCE;
		case "pickup":
			return PICKUP_PICKUP_CLEARANCE;
		default:
			return 0;
	}
}

/** A random point clear of planets, ships, spawn points and pods, or null after a few failed tries. */
function findClearSpot(world: WorldState): { x: number; y: number } | null {
	return randomClearSpot(world, pickupClearance);
}

/** Safe asteroid spawns (I-027): how far a new asteroid must stay from an entity. */
function asteroidClearance(e: Entity): number {
	switch (e.kind) {
		// A ship may respawn at a spawn point any moment: keep the same berth as from a ship.
		case "ship":
		case "spawnPoint":
			return ASTEROID_SPAWN_SHIP_CLEARANCE;
		case "planet":
		case "asteroid":
		case "pickup":
			return e.radius + ASTEROID_RADIUS;
		default:
			return 0;
	}
}

/**
 * A random point clear of planets, ships, spawn points, asteroids, pods and fueling stations,
 * or null after a few failed tries.
 */
function findClearAsteroidSpot(world: WorldState): { x: number; y: number } | null {
	const stations = world.fuelStations ?? [];
	const clearance = FUEL_STATION_RADIUS + ASTEROID_RADIUS;
	return randomClearSpot(world, asteroidClearance, (p) =>
		stations.every((s) => wrappedDistance(p, s.pos, world.size) > clearance),
	);
}

/** A random point where every entity's clearance (0 = no constraint) is satisfied. */
function randomClearSpot(
	world: WorldState,
	clearanceOf: (e: Entity) => number,
	clearOfOthers: (p: { x: number; y: number }) => boolean = () => true,
): { x: number; y: number } | null {
	const { width, height } = world.size;
	for (let attempt = 0; attempt < 12; attempt++) {
		const p = { x: nextFloat(world.rng) * width, y: nextFloat(world.rng) * height };
		const clear = world.entities.every((e) => {
			const clearance = clearanceOf(e);
			return clearance === 0 || wrappedDistance(p, e.pos, world.size) > clearance;
		});
		if (clear && clearOfOthers(p)) return p;
	}
	return null;
}
